import { motion } from "framer-motion";
import { useState, type JSX } from "react";

import { fadeRise } from "../lib/motion";
import { type UiProposalReview } from "../lib/types";
import { useVoss } from "../state/VossProvider";
import { Badge, Button } from "./primitives";

// M47 gate 1 — proposal acceptance. VOSS has planned and grounded a proposal, but NOTHING has been
// created or executed. The user reviews exactly what VOSS understood — capability, operation, the
// TRUSTED grounded target, why, reversibility, and whether execution will still require approval
// (gate 2) — then chooses to continue. Acceptance is by OPAQUE id only: the browser never holds or
// posts back a definition, target, params, or authorization detail. Everything shown here is
// provider-neutral: no provider/model, sandbox scope, roots/repos/allowlists, tokens, or secrets.
export function ProposalReviewCard({
  review,
  proposalId,
  closed,
}: {
  review: UiProposalReview;
  proposalId: string;
  closed?: "accepted" | "cancelled";
}): JSX.Element {
  const { acceptProposal, rejectProposal } = useVoss();
  const [busy, setBusy] = useState(false);

  const accept = (): void => {
    setBusy(true);
    void acceptProposal(proposalId).finally(() => setBusy(false));
  };
  const cancel = (): void => {
    setBusy(true);
    void rejectProposal(proposalId).finally(() => setBusy(false));
  };
  // "Adjust request" cancels this plan and returns focus to the composer so the user can rephrase.
  const adjust = (): void => {
    setBusy(true);
    void rejectProposal(proposalId).finally(() => {
      setBusy(false);
      document.querySelector<HTMLElement>('[aria-label="Message VOSS"]')?.focus();
    });
  };

  return (
    <motion.section
      className="voss-proposal"
      variants={fadeRise}
      initial="hidden"
      animate="visible"
      aria-label="Proposed plan"
      style={{ marginTop: 10, border: "1px solid var(--voss-border, rgba(255,255,255,0.12))", borderRadius: 14, padding: 14 }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ flex: 1, minWidth: 160 }}>VOSS understood this as…</strong>
        <Badge tone={closed === "accepted" ? "good" : closed === "cancelled" ? "neutral" : "accent"}>
          {closed === "accepted" ? "Mission created" : closed === "cancelled" ? "Cancelled" : "Proposed — not yet created"}
        </Badge>
      </header>
      <p style={{ margin: "6px 0 10px", opacity: 0.7, fontSize: 13 }}>
        “{review.title}”. Nothing has run yet.
        {review.requiresApproval ? " Each step will still ask for your approval before it runs." : ""}
      </p>

      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
        {review.steps.map((step, i) => (
          <li key={i} style={{ fontSize: 13, display: "grid", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ flex: 1 }}>
                <strong>{step.reason}</strong>{" "}
                <span style={{ opacity: 0.55 }}>
                  · {step.capability}
                  {step.operation ? ` · ${step.operation}` : ""}
                </span>
              </span>
              <Badge tone={step.requiresApproval ? "warn" : "neutral"}>{step.requiresApproval ? "needs approval" : "auto"}</Badge>
            </div>
            <div style={{ opacity: 0.8 }}>
              <strong>Target:</strong> <code>{step.target ?? "—"}</code> <span style={{ opacity: 0.6 }}>· {step.reversibility}</span>
            </div>
          </li>
        ))}
      </ol>

      {!closed ? (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <Button variant="primary" disabled={busy} onClick={accept}>
            Create mission
          </Button>
          <Button disabled={busy} onClick={cancel}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={adjust}>
            Adjust request
          </Button>
        </div>
      ) : (
        <p style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>
          <em>{closed === "accepted" ? "Mission created — see below." : "Proposal cancelled. Nothing ran."}</em>
        </p>
      )}
    </motion.section>
  );
}
